import React, { useState } from 'react';
import { ShieldCheck, Phone, Mail, MapPin, MessageCircle, X } from 'lucide-react';
import { COMPANY_INFO } from '../data/simatechData';
import { SimatechLogo } from './SimatechLogo';
import { useCms } from '../context/CmsContext';

interface FooterProps {
  onNavigate?: (page: any, targetId?: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const { siteData } = useCms();
  const brand = siteData?.brand || COMPANY_INFO;
  const footerData = siteData?.footer;
  const [isPrivacyOpen, setIsPrivacyOpen] = useState<boolean>(false);

  const defaultAboutText = 'Simatech Automation is a premier industrial automation system integrator and authorized Siemens Solution Partner, delivering turnkey PLC/SCADA engineering, custom control panel fabrication, and field commissioning worldwide.';
  const displayAboutText = (!footerData?.aboutText || footerData.aboutText.includes('Live test update verification'))
    ? defaultAboutText
    : footerData.aboutText;

  interface QuickLink {
    label: string;
    page: string;
    targetId?: string;
  }

  // Exact Quick Links as specified (2 Columns x 4 Rows)
  const quickLinksCol1: QuickLink[] = [
    { label: 'Home', page: 'home', targetId: 'home' },
    { label: 'About Us', page: 'about' },
    { label: 'Services', page: 'services' },
    { label: 'Industries', page: 'industry' },
  ];

  const quickLinksCol2: QuickLink[] = [
    { label: 'Products', page: 'product' },
    { label: 'Clients', page: 'clients' },
    { label: 'Contact', page: 'contact' },
    { label: 'Solution Partner', page: 'siemens-solution-partner' },
  ];

  const handleLink = (e: React.MouseEvent, page: any, targetId?: string) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(page, targetId);
    } else {
      if (page === 'siemens-solution-partner') {
        window.location.pathname = '/siemens-solution-partner';
      } else if (page === 'contact') {
        window.location.hash = 'contact';
      } else if (page === 'about') {
        window.location.hash = 'about';
      } else if (page === 'services') {
        window.location.hash = 'services';
      } else if (page === 'industry') {
        window.location.hash = 'industry';
      } else if (page === 'product') {
        window.location.hash = 'product';
      } else if (page === 'clients') {
        window.location.hash = 'clients';
      } else if (targetId) {
        window.location.hash = targetId;
      }
    }
  };

  return (
    <footer className="bg-[#041a1f] text-[#d4ebef]/85 text-sm border-t-2 border-[#1b7481]/50 relative overflow-hidden">
      
      {/* ========================================================================= */}
      {/* ARCHITECTURAL MATRIX & CIRCUIT PATTERN OVERLAY                             */}
      {/* ========================================================================= */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.045] z-0"
        style={{
          backgroundImage: `radial-gradient(#38c9de 1px, transparent 1px), radial-gradient(#1b7481 1px, #041a1f 1px)`,
          backgroundSize: '28px 28px',
          backgroundPosition: '0 0, 14px 14px',
        }}
      />
      
      {/* Ambient Glow Flares */}
      <div className="pointer-events-none absolute top-0 left-1/4 w-96 h-40 bg-[#1b7481]/20 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-10 w-96 h-48 bg-[#38c9de]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12 relative z-10">
        
        {/* ========================================================================= */}
        {/* MAIN 3-SECTION GRID: 1. Brand | 2. 4x4 Quick Links | 3. Office Address   */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 pb-14 border-b border-white/10">
          
          {/* COLUMN 1: BRAND OVERVIEW (Span 4) */}
          <div className="lg:col-span-4 space-y-5">
            <a
              href="#home"
              onClick={(e) => handleLink(e, 'home', 'home')}
              className="inline-block cursor-pointer"
              aria-label="Simatech Automation"
            >
              <SimatechLogo
                iconClassName="h-10 sm:h-11 w-auto"
                variant="white"
              />
            </a>

            <div
              className="text-sm font-light text-[#d4ebef]/85 leading-relaxed font-['General_Sans',sans-serif] max-w-sm sm:max-w-md"
              dangerouslySetInnerHTML={{
                __html: displayAboutText
              }}
            />

            {/* Social Media Channels */}
            <div className="pt-2">
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#d4ebef]/50 block mb-2.5">
                Connect With Us
              </span>
              <div className="flex items-center gap-3">
                {/* LinkedIn */}
                <a
                  href={footerData?.socialLinks?.linkedin || 'https://www.linkedin.com/company/simatech-automation/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Simatech Automation LinkedIn"
                  className="w-9 h-9 rounded-none bg-[#09292f] hover:bg-[#1b7481] text-[#81d8e5] hover:text-white flex items-center justify-center transition-all duration-300 border border-[#1b6f7e]/50 hover:shadow-[0_0_15px_rgba(56,201,222,0.4)]"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                  </svg>
                </a>

                {/* WhatsApp Direct */}
                <a
                  href={`https://wa.me/${(brand.phones?.[0] || '919324528303').replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Simatech Automation WhatsApp"
                  className="w-9 h-9 rounded-none bg-[#09292f] hover:bg-[#25D366] text-[#81d8e5] hover:text-white flex items-center justify-center transition-all duration-300 border border-[#1b6f7e]/50 hover:shadow-[0_0_15px_rgba(37,211,102,0.4)]"
                >
                  <MessageCircle className="w-4 h-4" />
                </a>

                {/* Facebook */}
                <a
                  href={footerData?.socialLinks?.facebook || 'https://www.facebook.com/simatechautomation/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Simatech Automation Facebook"
                  className="w-9 h-9 rounded-none bg-[#09292f] hover:bg-[#1877F2] text-[#81d8e5] hover:text-white flex items-center justify-center transition-all duration-300 border border-[#1b6f7e]/50 hover:shadow-[0_0_15px_rgba(24,119,242,0.4)]"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* COLUMN 2: QUICK LINKS (Span 4) */}
          <div className="lg:col-span-4 space-y-4">
            <h4 className="text-sm font-medium text-white tracking-wider uppercase font-['Jost',sans-serif] pb-2 border-b border-[#1b7481]/40 inline-block">
              Quick Links
            </h4>
            
            {/* 2-Column Exact Quick Links Grid */}
            <div className="grid grid-cols-2 gap-x-6 sm:gap-x-10 gap-y-3 pt-1 max-w-sm">
              <ul className="space-y-3 text-xs sm:text-[13px] font-light text-[#d4ebef]/85 font-['General_Sans',sans-serif]">
                {quickLinksCol1.map((link, idx) => (
                  <li key={idx}>
                    <a
                      href={`/${link.page === 'home' ? '' : link.page}${link.targetId && link.page !== 'home' ? `#${link.targetId}` : ''}`}
                      onClick={(e) => handleLink(e, link.page, link.targetId)}
                      className="hover:text-white hover:translate-x-0.5 inline-flex items-center gap-1.5 transition-all cursor-pointer leading-snug"
                    >
                      <span className="text-[#38c9de]/60 text-[10px]">›</span>
                      <span>{link.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <ul className="space-y-3 text-xs sm:text-[13px] font-light text-[#d4ebef]/85 font-['General_Sans',sans-serif]">
                {quickLinksCol2.map((link, idx) => (
                  <li key={idx}>
                    <a
                      href={`/${link.page}${link.targetId ? `#${link.targetId}` : ''}`}
                      onClick={(e) => handleLink(e, link.page, link.targetId)}
                      className="hover:text-white hover:translate-x-0.5 inline-flex items-center gap-1.5 transition-all cursor-pointer leading-snug"
                    >
                      <span className="text-[#38c9de]/60 text-[10px]">›</span>
                      <span>{link.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* COLUMN 3: OFFICE ADDRESS & DIRECT CONTACT (Span 3) */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-sm font-medium text-white tracking-wider uppercase font-['Jost',sans-serif] pb-2 border-b border-[#1b7481]/40 inline-block">
              Office Address
            </h4>
            <div className="text-xs space-y-3.5 text-[#d4ebef]/85 font-light font-['General_Sans',sans-serif]">
              
              {/* Office Address */}
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#38c9de] shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  {brand.address?.street ? `${brand.address.street}, ${brand.address.area}, ${brand.address.stateCountry}` : 'No. 3, Saraswati Bldg., Tungareshwar Ind. Complex, Sativali, Vasai (E), Thane – 401 208, Maharashtra, India'}
                </span>
              </div>

              {/* Direct Phone */}
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#38c9de] shrink-0" />
                <a
                  href="tel:+919820234033"
                  className="hover:text-white text-white font-normal transition-colors"
                >
                  {brand.phones?.[0] || COMPANY_INFO.phones[0]}
                </a>
              </div>

              {/* Official Email */}
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#38c9de] shrink-0" />
                <a
                  href={`mailto:${brand.emails?.[0] || COMPANY_INFO.emails[0]}`}
                  className="hover:text-white text-white transition-colors"
                >
                  {brand.emails?.[0] || COMPANY_INFO.emails[0]}
                </a>
              </div>

            </div>
          </div>

        </div>

        {/* ========================================================================= */}
        {/* BOTTOM BAR: COPYRIGHT, PRIVACY POLICY & DESIGN CREDIT                     */}
        {/* ========================================================================= */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-light text-[#d4ebef]/60 font-['General_Sans',sans-serif]">
          <div className="flex flex-wrap items-center gap-2">
            <span>{footerData?.copyright || `© ${new Date().getFullYear()} Simatech Automation. All rights reserved.`}</span>
            <span>•</span>
            <button
              type="button"
              onClick={() => setIsPrivacyOpen(true)}
              className="text-[#38c9de] hover:underline cursor-pointer"
            >
              Privacy Policy
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-[11px] font-mono">
            <span className="text-[#38c9de]">Siemens Authorized Channel Partner</span>
            <span>•</span>
            <span className="text-[#d4ebef]/80">
              Website Designed by{' '}
              <a
                href="https://excitetemplate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#38c9de] hover:text-white underline underline-offset-2 transition-colors font-medium"
              >
                Excite Template
              </a>
            </span>
          </div>
        </div>

      </div>

      {/* ========================================================================= */}
      {/* PRIVACY POLICY MODAL DIALOG                                               */}
      {/* ========================================================================= */}
      {isPrivacyOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fadeIn text-slate-800">
          <div className="bg-white rounded-2xl max-w-2xl w-full border border-slate-200 shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            
            {/* Modal Header */}
            <div className="px-6 py-4.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-[#1b7481]" />
                <h3 className="text-lg font-semibold text-[#06242a] font-['Jost',sans-serif]">
                  Privacy Policy &amp; Data Protection
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPrivacyOpen(false)}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 overflow-y-auto space-y-4 text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
              <p>
                At <strong>Simatech Automation</strong>, we take the confidentiality and privacy of our industrial clients, plant engineers, OEM partners, and website visitors with paramount seriousness.
              </p>
              
              <h4 className="font-semibold text-slate-900 text-sm pt-2">1. Industrial Inquiry &amp; RFQ Information</h4>
              <p>
                When you request a quotation, engineering review, or contact our team via simatechautomation.com, we collect details such as your name, corporate email, company/plant name, telephone number, and specific automation requirements. This data is exclusively used to process your technical requirements, provide Single-Line Diagrams (SLD), and generate commercial proposals.
              </p>

              <h4 className="font-semibold text-slate-900 text-sm pt-2">2. OEM &amp; Proprietary Engineering Confidentiality</h4>
              <p>
                We do not sell, rent, or lease customer data or technical project specifications to any third-party marketing entities. Any project files, CAD layouts, or industrial drawings shared with our engineering team are treated under strict Non-Disclosure Agreement (NDA) ethics.
              </p>

              <h4 className="font-semibold text-slate-900 text-sm pt-2">3. Cookies &amp; Telemetry</h4>
              <p>
                Our platform utilizes essential cookies to ensure optimal performance, secure session continuity, and seamless communication with our content delivery networks.
              </p>

              <h4 className="font-semibold text-slate-900 text-sm pt-2">4. Contact &amp; Grievance Redressal</h4>
              <p>
                If you have questions regarding our privacy practices or wish to review your stored inquiry data, please contact our compliance desk at <strong>simatech.automation@gmail.com</strong> or by post at our registered works: No. 3, Saraswati Bldg., Tungareshwar Ind. Complex, Sativali, Vasai (E), Thane – 401 208, Maharashtra, India.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={() => setIsPrivacyOpen(false)}
                className="px-5 py-2 rounded-lg bg-[#06242a] hover:bg-[#1b7481] text-white text-xs font-mono uppercase font-semibold tracking-wider transition-colors cursor-pointer"
              >
                Close Policy
              </button>
            </div>

          </div>
        </div>
      )}

    </footer>
  );
};
